"use client";

import clsx from "clsx";
import Link from "next/link";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Button from "@/components/library/Button";
import Icon, {IconNameExtended} from "@/components/library/Icon";
import {Skeleton} from "@/components/ui/skeleton";
import {CallByDayStat, CallsByTypeStat, ConversationByDayStat, StatsResponse} from "@/types/admin/dashboard";
import { apiLive } from "@/lib/api";
import { METABASE_STAT_KEYS, buildMetabaseStatApiPath } from "@/constants/metabaseStats";
import {GetDate_ISO} from "@/utils/common/generic";
import CommunicationMetricsBreakdown from "./CommunicationMetricsBreakdown";
import CommunicationMetricsChart from "./CommunicationMetricsChart";

type ChannelId = "phoneCall" | "sms" | "liveChat";

type Channel = {
   id: ChannelId;
   icon: IconNameExtended;
   enabled: boolean;
};

const CHANNELS: Channel[] = [
   {id: "phoneCall", icon: "phone", enabled: true},
   {id: "sms", icon: "mobile-screen", enabled: true},
   {id: "liveChat", icon: "message", enabled: true},
];

// Generate mock data for empty state display
const generateMockCallsByDay = (): CallByDayStat[] => {
   const mockCounts = [8000, 12000, 18000, 15000, 22000, 19000, 14000];

   return mockCounts.flatMap((totalCount, i) => {
      const date = new Date();
      date.setDate(date.getDate() - 6 + i);
      const dateString = GetDate_ISO(date);

      // Split count between inbound and outbound
      const inboundCount = Math.floor(totalCount * 0.6);
      const outboundCount = totalCount - inboundCount;

      return [
         {
            created_at: `${dateString}T00:00:00Z`,
            direction: "inbound" as const,
            count: inboundCount,
            duration: inboundCount * 180,
         },
         {
            created_at: `${dateString}T00:00:00Z`,
            direction: "outbound" as const,
            count: outboundCount,
            duration: outboundCount * 180,
         },
      ];
   });
};

const generateMockConversationsByDay = (): ConversationByDayStat[] => {
   const mockCounts = [120, 180, 250, 200, 310, 270, 190, 160, 220, 280, 240, 300, 260, 210];

   return mockCounts.map((count, i) => {
      const date = new Date();
      date.setDate(date.getDate() - 13 + i);
      const dateString = GetDate_ISO(date);
      return {
         created_at: `${dateString}T00:00:00Z`,
         count,
      };
   });
};

const generateMockCallsByType = (): CallsByTypeStat[] => {
   return [
      {category: "Claims", count: 710},
      {category: "Support", count: 60},
      {category: "Billing", count: 230},
   ];
};

const MOCK_CALLS_BY_DAY = generateMockCallsByDay();
const MOCK_CONVERSATIONS_BY_DAY = generateMockConversationsByDay();
const MOCK_CALLS_BY_TYPE = generateMockCallsByType();

export function CommunicationMetricsChartsSkeleton() {
   return (
      <div className={clsx("grid", "grid-cols-1", "@lg:grid-cols-2", "gap-8")}>
         <Skeleton className="h-[300px] rounded-lg" />
         <Skeleton className="h-[300px] rounded-lg" />
      </div>
   );
}

export default function CommunicationMetrics() {
   const {t} = useTranslation("stats");
   const [activeChannel, setActiveChannel] = useState<ChannelId>("phoneCall");
   const [isLoading, setIsLoading] = useState(true);

   const [callsByDay, setCallsByDay] = useState<CallByDayStat[] | null | undefined>(undefined);
   const [callsByType, setCallsByType] = useState<CallsByTypeStat[] | null | undefined>(undefined);
   const [smsByDay, setSmsByDay] = useState<ConversationByDayStat[] | null | undefined>(undefined);
   const [webchatsByDay, setWebchatsByDay] = useState<ConversationByDayStat[] | null | undefined>(
      undefined
   );

   useEffect(() => {
      const fetchStats = async () => {
         const [callsByDayRes, callsByTypeRes, smsByDayRes, webchatsByDayRes] =
            await Promise.allSettled([
               apiLive<StatsResponse<CallByDayStat[]>>(
                  buildMetabaseStatApiPath(METABASE_STAT_KEYS.callsByDay)
               ),
               apiLive<StatsResponse<CallsByTypeStat[]>>(
                  buildMetabaseStatApiPath(METABASE_STAT_KEYS.callsByType)
               ),
               apiLive<StatsResponse<ConversationByDayStat[]>>(
                  buildMetabaseStatApiPath(METABASE_STAT_KEYS.smsByDay)
               ),
               apiLive<StatsResponse<ConversationByDayStat[]>>(
                  buildMetabaseStatApiPath(METABASE_STAT_KEYS.webchatByDay)
               ),
            ]);

         const resolve = <T,>(result: PromiseSettledResult<T | undefined>): T | null =>
            result.status === "fulfilled" && result.value ? result.value : null;

         const callsByDay = resolve(callsByDayRes);
         const callsByType = resolve(callsByTypeRes);
         const smsByDay = resolve(smsByDayRes);
         const webchatsByDay = resolve(webchatsByDayRes);

         setCallsByDay(callsByDay?.success ? callsByDay.stats : null);
         setCallsByType(callsByType?.success ? callsByType.stats : null);
         setSmsByDay(smsByDay?.success ? smsByDay.stats : null);
         setWebchatsByDay(webchatsByDay?.success ? webchatsByDay.stats : null);
         setIsLoading(false);
      };

      fetchStats();
   }, []);

   // Determine if we have data per channel
   const hasCallsByDay = callsByDay && callsByDay.length > 0;
   const hasCallsByType = callsByType && callsByType.length > 0;
   const hasSmsByDay = smsByDay && smsByDay.length > 0;
   const hasWebchatsByDay = webchatsByDay && webchatsByDay.length > 0;

   // Determine if the active channel has data
   const getActiveChannelHasData = () => {
      switch (activeChannel) {
         case "phoneCall":
            return hasCallsByDay || hasCallsByType;
         case "sms":
            return hasSmsByDay;
         case "liveChat":
            return hasWebchatsByDay;
      }
   };
   const activeChannelHasData = getActiveChannelHasData();

   // Use mock data when no real data is available, shown behind a blur effect
   const displayCallsByDay = hasCallsByDay ? callsByDay : MOCK_CALLS_BY_DAY;
   const displayCallsByType = hasCallsByType ? callsByType : MOCK_CALLS_BY_TYPE;
   const displaySmsByDay = hasSmsByDay ? smsByDay : MOCK_CONVERSATIONS_BY_DAY;
   const displayWebchatsByDay = hasWebchatsByDay ? webchatsByDay : MOCK_CONVERSATIONS_BY_DAY;

   return (
      <div
         className={clsx(
            "bg-panel",
            "rounded-xl",
            "p-6",
            "relative",
            "overflow-hidden",
            "border border-neutral"
         )}
      >
         {/* Header */}
         <div
            className={clsx(
               "flex",
               "flex-col",
               "@sm:flex-row",
               "@sm:items-center",
               "@sm:justify-between",
               "gap-4",
               "mb-6"
            )}
         >
            <h2 className={clsx("text-xl", "font-medium", "text-900")}>
               {t("communicationMetrics.title")}
            </h2>
            <Link
               href="/reporting/dashboard"
               className={clsx(
                  "flex",
                  "items-center",
                  "gap-1",
                  "text-primary",
                  "font-medium",
                  "text-sm",
                  "hover:opacity-80"
               )}
            >
               {t("communicationMetrics.seeMore")}
               <Icon icon="arrow-right" className="size-4" />
            </Link>
         </div>

         {/* Channel Filters and Date Range */}
         <div
            className={clsx(
               "flex",
               "flex-col",
               "@lg:flex-row",
               "@lg:items-center",
               "@lg:justify-between",
               "gap-4",
               "mb-6"
            )}
         >
            {/* Channel Pills */}
            <div className={clsx("flex", "flex-wrap", "gap-2")}>
               {CHANNELS.map((channel) => {
                  const isActive = activeChannel === channel.id;
                  return (
                     <button
                        key={channel.id}
                        disabled={!channel.enabled}
                        onClick={() => channel.enabled && setActiveChannel(channel.id)}
                        className={clsx(
                           "flex",
                           "items-center",
                           "gap-2",
                           "px-4",
                           "py-2",
                           "rounded-full",
                           "border",
                           "text-xs",
                           "font-medium",
                           "transition-colors",
                           {
                              "border-primary text-primary bg-primary/10": isActive,
                              "bg-transparent text-600 border-300 hover:border-primary hover:text-primary":
                                 !isActive && channel.enabled,
                              "bg-transparent text-600 border-300 opacity-50 cursor-not-allowed":
                                 !channel.enabled,
                           }
                        )}
                     >
                        <Icon icon={channel.icon} className="size-4" />
                        {t(`communicationMetrics.channels.${channel.id}`)}
                     </button>
                  );
               })}
            </div>
         </div>

         {/* Charts Container */}
         <div className={clsx("relative")}>
            {isLoading ? (
               <CommunicationMetricsChartsSkeleton />
            ) : (
               <>
                  {/* Charts Grid - always show charts with mock or real data */}
                  <div
                     className={clsx("grid", "grid-cols-1", "gap-8", {
                        "@lg:grid-cols-2": activeChannel === "phoneCall",
                        "blur-sm pointer-events-none select-none": !activeChannelHasData,
                     })}
                  >
                     {activeChannel === "phoneCall" && (
                        <>
                           {/* Calls Over Time Chart */}
                           <div className={clsx("min-h-[300px]")}>
                              <CommunicationMetricsChart
                                 callsByDay={displayCallsByDay}
                                 channelId="phoneCall"
                              />
                           </div>

                           {/* Call Type Breakdown Chart */}
                           <div className={clsx("min-h-[300px]")}>
                              <CommunicationMetricsBreakdown callsByType={displayCallsByType} />
                           </div>
                        </>
                     )}

                     {activeChannel === "sms" && (
                        <div className={clsx("min-h-[300px]")}>
                           <CommunicationMetricsChart
                              conversationsByDay={displaySmsByDay}
                              channelId="sms"
                              days={14}
                           />
                        </div>
                     )}

                     {activeChannel === "liveChat" && (
                        <div className={clsx("min-h-[300px]")}>
                           <CommunicationMetricsChart
                              conversationsByDay={displayWebchatsByDay}
                              channelId="liveChat"
                              days={14}
                           />
                        </div>
                     )}
                  </div>

                  {/* Empty State Overlay */}
                  {!activeChannelHasData && (
                     <div
                        className={clsx(
                           "absolute",
                           "inset-0",
                           "flex",
                           "items-center",
                           "justify-center"
                        )}
                     >
                        <Button type="primary" href="/inbound/scripts" className="shadow-xl">
                           {t("communicationMetrics.getStarted")}
                        </Button>
                     </div>
                  )}
               </>
            )}
         </div>
      </div>
   );
}
